'use client';

import { Flex, Button, createListCollection } from '@chakra-ui/react';
import { useState, useMemo } from 'react';
import { PRODUCT_OPTIONS } from '@/constants/product/productOptions';
import ProductFinderSelect from './ProductFinderSelect';
import { useSafeRouter } from '@/hooks/route/useSafeRouter';
import { useSkinType } from '@/hooks/product/useSkinType';
import { useSkinConcern } from '@/hooks/product/useSkinConcern';
import { generateUrl } from '../utils/urlGenerator';
import { createTermIdExtractor } from '../utils/taxonomyUtils';
import { useProductService } from '@/hooks/product/useProductService';
import { convertToPascalCase } from '@/utils/function';

const ProductFinderContent = () => {
  const router = useSafeRouter();
  const skinTypeOptions = useSkinType();
  const skinConcernOptions = useSkinConcern();

  // Get raw taxonomy data to extract term IDs
  const { useGetProductTaxonomy } = useProductService();
  const { data: taxonomy } = useGetProductTaxonomy();

  const [productType, setProductType] = useState<string[]>([PRODUCT_OPTIONS[0].value]);
  const [skinType, setSkinType] = useState<string[]>(['all']);
  const [skinConcern, setSkinConcern] = useState<string[]>(['all']);

  // Create collections for the select components
  const productCollection = createListCollection({
    items: PRODUCT_OPTIONS.map((option) => ({
      label: option.label,
      value: option.value,
    })),
  });

  // Create skin type collection
  const skinTypeCollection = useMemo(() => {
    return createListCollection({
      items: skinTypeOptions,
    });
  }, [skinTypeOptions]);

  // Create skin concern collection
  const concernCollection = useMemo(() => {
    return createListCollection({
      items: skinConcernOptions.map((option) => ({
        label: convertToPascalCase(option.label),
        value: option.value,
      })),
    });
  }, [skinConcernOptions]);

  const getSkinTypeTermId = useMemo(() => createTermIdExtractor(taxonomy?.skinType), [taxonomy]);

  const getSkinConcernTermId = useMemo(() => createTermIdExtractor(taxonomy?.skinConcern), [taxonomy]);

  const handleShowSolutions = () => {
    const url = generateUrl({
      productType: productType[0],
      skinType: skinType[0],
      skinConcern: skinConcern[0],
      getSkinTypeTermId,
      getSkinConcernTermId,
    });
    router.push(url);
  };

  return (
    <>
      <Flex
        direction={{ base: 'column', mobile: 'row' }}
        gap={{ base: 8, mobile: 4, tablet: 10, desktop: 16 }}
        justifyContent="space-between"
        width="100%"
      >
        <ProductFinderSelect
          label="I'm looking for"
          collection={productCollection}
          value={productType}
          onValueChange={(details) => setProductType(details.value)}
        />

        <ProductFinderSelect
          label="My skin type is"
          collection={skinTypeCollection}
          value={skinType}
          onValueChange={(details) => setSkinType(details.value)}
        />

        <ProductFinderSelect
          label="My skin concern is"
          collection={concernCollection}
          value={skinConcern}
          onValueChange={(details) => setSkinConcern(details.value)}
        />
      </Flex>

      <Button
        backgroundColor="common.100"
        border="1px solid"
        borderColor="brand.50"
        borderRadius="8px"
        color="brand.50"
        textStyle="body3"
        fontWeight="regular"
        height="48px"
        width={{ base: 'full', tablet: '332px' }}
        _hover={{
          backgroundColor: 'brand.50',
          color: 'common.100',
          borderColor: 'common.100',
        }}
        onClick={handleShowSolutions}
        alignSelf="center"
      >
        Show my solutions
      </Button>
    </>
  );
};

export default ProductFinderContent;
